import React, { useEffect, useState } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    Button
} from '@mui/material';
import {
    Chat as ChatIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';

import { useChatSessions } from '../hooks/useChatSessions';



const SessionsList = ({ 
    onSessionSelect, 
    currentSessionId, 
    onNewSession,
    isCollapsed = false 
}) => {
    const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
    
    const {
        sessions,
        isLoadingSessions,
        sessionsError,
        loadSessions,
        refreshSessions,
        hasSessionsData,
        sessionsStats
    } = useChatSessions({
        autoRefresh: true,
        refreshInterval: 30000,
        refreshOnFocus: true
    });

    // Load sessions when component mounts
    useEffect(() => {
        loadSessions().then(() => {
            setLastRefreshTime(Date.now());
        });
    }, [loadSessions]);

    // Update last refresh time when sessions change
    useEffect(() => {
        if (sessions.length > 0) {
            setLastRefreshTime(Date.now());
        }
    }, [sessions.length]);

    const handleSessionClick = (session) => {
        onSessionSelect(session.session_id);
    };

    const handleRefresh = () => {
        refreshSessions();
        setLastRefreshTime(Date.now());
    };

    const formatLastRefreshTime = () => {
        const now = new Date();
        const refreshTime = new Date(lastRefreshTime);
        const diffInMinutes = Math.floor((now - refreshTime) / (1000 * 60));
        
        if (diffInMinutes < 1) {
            return 'just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} min ago`;
        } else if (diffInMinutes < 24 * 60) {
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
        } else {
            const days = Math.floor(diffInMinutes / (24 * 60));
            return `${days} ${days === 1 ? 'day' : 'days'} ago`;
        }
    };

    const formatSessionTime = (dateTime) => {
        const now = new Date();
        const sessionTime = new Date(dateTime);
        const diffInMinutes = Math.floor((now - sessionTime) / (1000 * 60));
        
        if (diffInMinutes < 1) {
            return 'just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} min ago`;
        } else if (diffInMinutes < 24 * 60) {
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
        } else {
            const days = Math.floor(diffInMinutes / (24 * 60));
            return `${days} ${days === 1 ? 'day' : 'days'} ago`;
        }
    };

    const truncatePreview = (text, maxLength = 60) => {
        if (!text) return 'No messages yet';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    return (
        <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRadius: 1
        }}>
            {/* Header */}
            <Box sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChatIcon />
                    {!isCollapsed && 'Your Chats'}
                </Typography>
                
                {!isCollapsed && (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title={`Manual refresh (updated ${formatLastRefreshTime()})`}>
                            <IconButton 
                                size="small" 
                                onClick={handleRefresh} 
                                disabled={isLoadingSessions}
                                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                                <RefreshIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="New session">
                            <IconButton size="small" onClick={onNewSession} color="primary">
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Stats */}
            {!isCollapsed && hasSessionsData && (
                <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">
                        {sessionsStats.totalSessions} sessions • {sessionsStats.totalMessages} total messages
                    </Typography>
                </Box>
            )}

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {/* Loading State */}
                {isLoadingSessions && sessions.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {/* Error State */}
                {sessionsError && (
                    <Alert 
                        severity="error" 
                        sx={{ m: 2 }}
                        action={
                            <Button size="small" onClick={handleRefresh}>
                                Retry
                            </Button>
                        }
                    >
                        {sessionsError}
                    </Alert>
                )}

                {/* Empty State */}
                {!isLoadingSessions && !sessionsError && sessions.length === 0 && (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: 200,
                        textAlign: 'center',
                        px: 2
                    }}>
                        <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            No chat sessions yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Start a conversation to create your first session
                        </Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={onNewSession}>
                            New Session
                        </Button>
                    </Box>
                )}

                {/* Sessions List */}
                {sessions.length > 0 && (
                    <List sx={{ py: 0, overflow: 'auto', height: '100%' }}>
                        {sessions.map((session, index) => (
                            <React.Fragment key={session.session_id}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        selected={currentSessionId === session.session_id}
                                        onClick={() => handleSessionClick(session)}
                                        sx={{ 
                                            py: isCollapsed ? 1 : 1.5,
                                            px: 2,
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.lighter',
                                                borderRight: 3,
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        {isCollapsed ? (
                                            <Tooltip 
                                                title={`Session from ${formatSessionTime(session.started_at)}`}
                                                placement="right"
                                            >
                                                <ChatIcon color={currentSessionId === session.session_id ? 'primary' : 'action'} />
                                            </Tooltip>
                                        ) : (
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" component="span" noWrap sx={{ flex: 1 }}>
                                                            {truncatePreview(session.last_message_preview)}
                                                        </Typography>
                                                        <Chip 
                                                            label={session.message_count} 
                                                            size="small" 
                                                            variant="outlined"
                                                            sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                        <TimeIcon sx={{ fontSize: 14 }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatSessionTime(session.last_activity_at)}
                                                        </Typography>
                                                        {!session.is_active && (
                                                            <Chip 
                                                                label="Inactive" 
                                                                size="small" 
                                                                color="error"
                                                                variant="outlined"
                                                                sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                                {index < sessions.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

export default SessionsList; 